import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Textarea } from "@src/lib/ui/Textarea";
import { Button } from "@src/lib/ui/Button";
import { useCreateDashboardFeedbackMutation } from "@src/features/app/top-nav/HelpButton.generated";
import { useToast } from "@src/lib/hooks/useToast";
import { useTranslation } from "@src/features/contentDetail/utils/translationSets";
import useLanguage from "@src/features/contentDetail/hooks/useLanguage";

const SUBMIT_FEEDBACK_ENGLISH = "Submit feedback";
const FEEDBACK_SUBMITTED_ENGLISH = "Feedback submitted";

interface SubmitFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SubmitFeedbackDialog: React.FC<SubmitFeedbackDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [feedback, setFeedback] = React.useState("");
  const { addErrorToast, addToast } = useToast();
  const language = useLanguage();
  const submitFeedbackTranslation = useTranslation(
    SUBMIT_FEEDBACK_ENGLISH,
    language || "en",
  );
  const feedbackSubmittedTranslation = useTranslation(
    FEEDBACK_SUBMITTED_ENGLISH,
    language || "en",
  );
  const [createDashboardFeedback, { loading }] =
    useCreateDashboardFeedbackMutation({
      onCompleted: (data) => {
        if (data.createDashboardFeedback.success) {
          addToast({
            message:
              feedbackSubmittedTranslation.text || FEEDBACK_SUBMITTED_ENGLISH,
          });
          onOpenChange(false);
          setFeedback("");
        } else {
          addErrorToast({
            data,
            callsite: "dashboard_feedback_form",
          });
        }
      },
    });

  const handleSubmit = React.useCallback(() => {
    createDashboardFeedback({ variables: { comment: feedback } });
  }, [createDashboardFeedback, feedback]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {submitFeedbackTranslation.text || SUBMIT_FEEDBACK_ENGLISH}
          </DialogTitle>
          <DialogDescription>
            Let us know how we can improve your experience.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder="Your feedback..."
          className="min-h-32 resize-y"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={!feedback.trim() || loading}>
            {submitFeedbackTranslation.text || SUBMIT_FEEDBACK_ENGLISH}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
